// "Make bigger" [Agrandar] (M13): one tap for 2× or 4× (capped at 24 MP), tap the pressed one again to go
// back to the original size. Shared by Simple mode and the Pro Upscale panel. One undo step per tap.
import { Check, ZoomIn } from 'lucide-react';
import { useT } from '../../i18n';
import { useLibrary } from '../../state/library';
import { useEditor } from '../../state/editor';
import { makeBigger, upscalePreview, useUpscale } from '../../features/upscale/upscale';

export function MakeBigger({ big = true }: { big?: boolean }) {
  const t = useT();
  const id = useLibrary((s) => s.activeId);
  const photo = useLibrary((s) => s.photos.find((p) => p.id === s.activeId) ?? null);
  const factor = useEditor((s) => (id ? s.settings[id]?.upscale.factor : undefined)) ?? 1;
  const st = useUpscale((u) => (id ? u.status[id] : undefined));
  const running = st?.state === 'running';
  const preview2 = photo ? upscalePreview(photo, 2) : null;
  const tooLarge = !!photo && photo.width > 0 && !preview2;
  const shown = photo ? upscalePreview(photo, factor > 1 ? factor : 4) : null;
  const h = big ? 48 : 32;

  const tap = (f: 2 | 4) => {
    if (!id) return;
    void makeBigger(id, factor === f ? 1 : f);
  };

  return (
    <div data-testid="make-bigger">
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {big && (
          <span style={{ flex: '0 0 auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <ZoomIn className="icon" aria-hidden="true" /> {t('makeBigger')}
          </span>
        )}
        {([2, 4] as const).map((f) => (
          <button
            key={f}
            data-testid={`make-bigger-${f}`}
            aria-pressed={factor === f}
            disabled={!photo || tooLarge || running}
            onClick={() => tap(f)}
            style={{
              minHeight: h,
              flex: 1,
              ...(factor === f ? { background: 'var(--accent-fill)', color: 'var(--on-accent)' } : {}),
            }}
          >
            {factor === f && <Check className="icon" aria-hidden="true" />}
            {t(f === 2 ? 'makeBigger2' : 'makeBigger4')}
          </button>
        ))}
      </div>
      <div
        style={{ fontSize: 12, marginTop: 4, color: 'var(--muted)' }}
        role="status"
        data-testid="make-bigger-status"
      >
        {tooLarge
          ? t('makeBiggerTooLarge')
          : running
            ? t('makeBiggerRunning', { done: st!.done, total: st!.total || '…' })
            : st?.state === 'error'
              ? t('makeBiggerFailed', { reason: st.message ?? '' })
              : factor > 1 && shown
                ? t('makeBiggerDone', { w: shown.width, h: shown.height })
                : photo && photo.width > 0 && preview2
                  ? t('makeBiggerSize', {
                      w: photo.width,
                      h: photo.height,
                      W: upscalePreview(photo, 4)!.width,
                      H: upscalePreview(photo, 4)!.height,
                    })
                  : ''}
      </div>
    </div>
  );
}
